import { atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import { match } from 'ts-pattern'

import { Network } from '@root/entities/network'
import { Socials } from '@root/entities/socials'
import { environmentAtom } from '@root/shared'
import {
  ETH_LOGO,
  SOL_LOGO,
  TON_LOGO,
  USDC_LOGO,
  USDT_LOGO,
} from '@root/shared/assets'

import { CurrencyAddress } from './CurrencyAddress'
import { ImageHash } from './image-hash'

import type * as CurrencySnapshot from './currency-snapshot'
import type { Equivalence } from '@blade/std-lib'
import type { AtomMut, Ctx } from '@reatom/framework'
import type { PremiumApi, ServicesApi, WalletApi } from '@root/shared/api'
import type { TokenLogosHost } from '@root/shared/models'

export type IconModerationStatus = NonNullable<
  ServicesApi.TokenMetaDto['status']
>
export type ModerationReason = NonNullable<
  ServicesApi.TokenMetaDto['moderationReason']
>

type TokenMetaDto =
  | ServicesApi.TokenMetaDto
  | PremiumApi.TokenMetaWithPremiumDto
  | ServicesApi.ExtendedTokenMetaDto

export type CurrencyModelInit = {
  address: CurrencyAddress
  network: Network.Network
  ticker: string
  title: string
  decimals: number
  reusedImageCount: number | undefined
  imageHash: ImageHash | undefined
  socials: Socials.Socials
  icon: string | undefined
  iconStatus: IconModerationStatus | undefined
  moderationReason: ModerationReason | undefined
  supply: BigNumber
  updatedAt: Date | undefined
}

export class CurrencyModel {
  static Currency = CurrencyModel
  public readonly address: CurrencyAddress
  public readonly network: Network.Network
  public readonly ticker: string
  public readonly title: string
  public readonly decimals: number
  public readonly reusedImageCount: number | undefined
  public readonly imageHash: ImageHash | undefined
  public readonly icon: AtomMut<string | undefined>
  public readonly iconStatus: AtomMut<IconModerationStatus | undefined>
  public readonly moderationReason: AtomMut<ModerationReason | undefined>
  public readonly socials: AtomMut<Socials.Socials>
  public readonly updatedAt: AtomMut<Date | undefined>
  public readonly supply: BigNumber

  constructor(init: CurrencyModelInit) {
    this.address = init.address
    this.network = init.network
    this.ticker = init.ticker
    this.title = init.title
    this.decimals = init.decimals
    this.reusedImageCount = init.reusedImageCount
    this.imageHash = init.imageHash
    this.supply = init.supply
    this.icon = atom(init.icon, 'icon')
    this.iconStatus = atom(init.iconStatus, 'iconStatus')
    this.socials = atom(init.socials, 'socials')
    this.moderationReason = atom(init.moderationReason, 'moderationReason')
    this.updatedAt = atom(init.updatedAt, 'updatedAt')
  }

  public updateFromTokenMeta = (ctx: Ctx, dto: ServicesApi.TokenMetaDto) => {
    const {
      assets: { tokenLogosHost },
    } = ctx.get(environmentAtom)

    const currentUpdatedAt = ctx.get(this.updatedAt)
    const updatedAt = dto.updatedAt ? new Date(dto.updatedAt) : undefined

    if (currentUpdatedAt) {
      if (!updatedAt) {
        return
      }

      if (currentUpdatedAt > updatedAt) {
        return
      }
    }

    this.iconStatus(ctx, dto.status)

    if (updatedAt) {
      this.updatedAt(ctx, updatedAt)
    }

    this.icon(
      ctx,
      makeIconUrl(dto.logo, tokenLogosHost, dto.symbol, dto.address),
    )
    if (dto.socials) {
      this.socials(ctx, Socials.fromDto(dto.socials))
    }
  }

  static fromTokenMeta = (dto: TokenMetaDto, imagesHost: TokenLogosHost) => {
    const network = Network.fromDto(dto.network)
    if (dto.address === 'native') {
      return CurrencyModel.nativeOf(network)
    }

    const model = new CurrencyModel({
      address: CurrencyAddress(dto.address),
      network,
      ticker: dto.symbol || '',
      title: titleFromDto(dto.symbol, dto.address, dto.name),
      decimals: dto.decimals,
      reusedImageCount:
        'reusedImageCount' in dto ? dto.reusedImageCount : undefined,
      imageHash:
        'imageHash' in dto && dto.imageHash
          ? ImageHash(dto.imageHash)
          : undefined,
      socials: dto.socials ? Socials.fromDto(dto.socials) : Socials.empty(),
      icon: makeIconUrl(dto.logo, imagesHost, dto.symbol, dto.address),
      iconStatus: dto.status,
      moderationReason:
        'moderationReason' in dto ? dto.moderationReason : undefined,
      supply: BigNumber(dto.supply),
      updatedAt:
        'updatedAt' in dto && dto.updatedAt
          ? new Date(dto.updatedAt)
          : undefined,
    })
    return model
  }

  static fromTxTokenMeta = (
    dto: ServicesApi.TradeTxDto['tokenMetadata'],
    network: Network.Network,
    imagesHost: TokenLogosHost,
  ) => {
    if (!dto) {
      return undefined
    }

    if (dto.address === 'native') {
      return CurrencyModel.nativeOf(network)
    }

    const model = new CurrencyModel({
      address: CurrencyAddress(dto.address),
      network,
      ticker: dto.symbol || '',
      title: titleFromDto(dto.symbol, dto.address, dto.name),
      decimals: dto.decimals,
      reusedImageCount: dto.reusedImageCount,
      imageHash: dto.imageHash ? ImageHash(dto.imageHash) : undefined,
      socials: Socials.empty(),
      icon: makeIconUrl(dto.logo, imagesHost, dto.symbol, dto.address),
      iconStatus: dto.status,
      moderationReason: dto.moderationReason,
      supply: BigNumber(dto.supply),
      updatedAt: undefined,
    })
    return model
  }

  static fromWalletAssetDto = (
    dto: WalletApi.WalletAssetDto,
    imagesHost: TokenLogosHost,
    network: Network.Network,
  ) => {
    if (dto.address === 'native') {
      return CurrencyModel.nativeOf(network)
    }

    const model = new CurrencyModel({
      address: CurrencyAddress(dto.address),
      network,
      ticker: dto.symbol || '',
      title: titleFromDto(dto.symbol, dto.address, dto.name),
      decimals: dto.decimals,
      reusedImageCount: undefined,
      imageHash: undefined,
      socials: Socials.empty(),
      icon: makeIconUrl(dto.logo, imagesHost, dto.symbol, dto.address),
      iconStatus: dto.logoStatus,
      moderationReason: undefined,
      supply: BigNumber(0),
      updatedAt: undefined,
    })
    return model
  }

  static toSnapshot = (
    ctx: Ctx,
    currency: CurrencyModel,
  ): CurrencySnapshot.CurrencySnapshot => ({
    address: currency.address,
    network: currency.network,
    ticker: currency.ticker,
    title: currency.title,
    decimals: currency.decimals,
    reusedImageCount: currency.reusedImageCount,
    imageHash: currency.imageHash,
    icon: ctx.get(currency.icon),
    iconStatus: ctx.get(currency.iconStatus),
    moderationReason: ctx.get(currency.moderationReason),
    supply: currency.supply,
    updatedAt: ctx.get(currency.updatedAt),
  })

  static fromSnapshot = (
    snapshot: CurrencySnapshot.CurrencySnapshot,
  ): CurrencyModel => {
    return new CurrencyModel({
      address: CurrencyAddress(snapshot.address),
      network: snapshot.network,
      ticker: snapshot.ticker,
      title: snapshot.title,
      decimals: snapshot.decimals,
      reusedImageCount: snapshot.reusedImageCount,
      imageHash: snapshot.imageHash ? ImageHash(snapshot.imageHash) : undefined,
      socials: Socials.empty(),
      icon: snapshot.icon,
      iconStatus: snapshot.iconStatus,
      moderationReason: snapshot.moderationReason,
      supply: snapshot.supply,
      updatedAt: snapshot.updatedAt,
    })
  }

  static fromPairCreatorStatsTokenDto = (
    dto: ServicesApi.PairCreatorStatsTokenDto,
    imagesHost: TokenLogosHost,
    network: Network.Network,
  ) => {
    if (dto.address === 'native') {
      return CurrencyModel.nativeOf(network)
    }

    const model = new CurrencyModel({
      address: CurrencyAddress(dto.address),
      network,
      ticker: dto.symbol || '',
      title: titleFromDto(dto.symbol, dto.address, dto.name),
      decimals: 0,
      reusedImageCount: undefined,
      imageHash: undefined,
      socials: Socials.empty(),
      icon: makeIconUrl(dto.logo, imagesHost, dto.symbol, dto.address),
      iconStatus: dto.status ?? 'active',
      moderationReason: dto.moderationReason,
      supply: BigNumber(0),
      updatedAt: undefined,
    })
    return model
  }

  static calculateFdv = (currency: CurrencyModel, priceUsd: BigNumber) =>
    priceUsd.multipliedBy(currency.supply)

  static calculatePriceFromMarketCap = (
    currency: CurrencyModel,
    marketCap: BigNumber,
  ) => {
    return marketCap.dividedBy(currency.supply)
  }

  static match = (query: string) => (currency: CurrencyModel) => {
    const resolvedQuery = query.trim().toLowerCase()
    return [currency.address, currency.title, currency.ticker].some((x) =>
      x.toLowerCase().includes(resolvedQuery),
    )
  }

  static searchCurrencies =
    (query: string) => (currencies: Array<CurrencyModel>) => {
      return currencies.filter(CurrencyModel.match(query))
    }

  static eq: Equivalence.Equivalence<CurrencyModel> = (left, right) =>
    left.address === right.address && left.network === right.network

  static isSolOrWsol = (currency: CurrencyModel) =>
    CurrencyModel.eq(currency, CurrencyModel.Sol) ||
    CurrencyModel.eq(currency, CurrencyModel.WrappedSol) ||
    CurrencyModel.eq(currency, CurrencyModel.PumpFunSol)

  static isEthOrWeth = (currency: CurrencyModel) =>
    CurrencyModel.eq(currency, CurrencyModel.Ether) ||
    CurrencyModel.eq(currency, CurrencyModel.WrappedEther)

  static isBaseEthOrWeth = (currency: CurrencyModel) =>
    CurrencyModel.eq(currency, CurrencyModel.EtherBase) ||
    CurrencyModel.eq(currency, CurrencyModel.WrappedEtherBase)

  static isBnbOrWbnb = (currency: CurrencyModel) =>
    CurrencyModel.eq(currency, CurrencyModel.Bnb) ||
    CurrencyModel.eq(currency, CurrencyModel.WrappedBnb)

  static isTon = (currency: CurrencyModel) =>
    CurrencyModel.eq(currency, CurrencyModel.Ton)

  static isUsdStableCurrency = (currency: CurrencyModel) =>
    CurrencyModel.eq(currency, CurrencyModel.UsdcSol) ||
    CurrencyModel.eq(currency, CurrencyModel.UsdtSol) ||
    CurrencyModel.eq(currency, CurrencyModel.UsdtEvm) ||
    CurrencyModel.eq(currency, CurrencyModel.UsdtTon) ||
    CurrencyModel.eq(currency, CurrencyModel.UsdtBase) ||
    CurrencyModel.eq(currency, CurrencyModel.UsdtBsc) ||
    CurrencyModel.eq(currency, CurrencyModel.PyusdSol) ||
    CurrencyModel.eq(currency, CurrencyModel.UsdgSol) ||
    CurrencyModel.eq(currency, CurrencyModel.Usd1Sol) ||
    CurrencyModel.eq(currency, CurrencyModel.UsxSol) ||
    CurrencyModel.eq(currency, CurrencyModel.FdusdSol) ||
    CurrencyModel.eq(currency, CurrencyModel.UsdySol)

  static findStableCurrency = (currency: CurrencyModel) => {
    return CurrencyModel.stableCurrencies.find((c) =>
      CurrencyModel.eq(currency, c),
    )
  }

  static isNative = (currency: CurrencyModel) =>
    CurrencyModel.eq(currency, CurrencyModel.nativeOf(currency.network))

  static isNativeOrWrapped = (network: Network.Network) =>
    match(network)
      .with(Network.Solana, () => CurrencyModel.isSolOrWsol)
      .with(Network.Ethereum, () => CurrencyModel.isEthOrWeth)
      .with(Network.Base, () => CurrencyModel.isBaseEthOrWeth)
      .with(Network.Bsc, () => CurrencyModel.isBnbOrWbnb)
      .with(Network.Ton, () => CurrencyModel.isTon)
      .exhaustive()

  static toNativeIfWrapped = (currency: CurrencyModel) =>
    CurrencyModel.isNativeOrWrapped(currency.network)(currency)
      ? CurrencyModel.nativeOf(currency.network)
      : currency

  static nativeOf = (network: Network.Network) =>
    match(network)
      .with(Network.Solana, () => CurrencyModel.Sol)
      .with(Network.Ethereum, () => CurrencyModel.Ether)
      .with(Network.Base, () => CurrencyModel.EtherBase)
      .with(Network.Bsc, () => CurrencyModel.Bnb)
      .with(Network.Ton, () => CurrencyModel.Ton)
      .exhaustive()

  static convertAmountToBigInt =
    (currency: CurrencyModel | { decimals: number }) => (amount: BigNumber) => {
      return BigNumber(amount)
        .multipliedBy(BigNumber(10).exponentiatedBy(currency.decimals))
        .integerValue(BigNumber.ROUND_FLOOR)
    }
  static convertBigIntToAmount =
    (currency: CurrencyModel | { decimals: number }) =>
    (amount: bigint | number) => {
      return BigNumber(amount.toString()).dividedBy(
        BigNumber(10).exponentiatedBy(currency.decimals),
      )
    }

  static solNullCurrency = new CurrencyModel({
    address: CurrencyAddress('null'),
    network: Network.Solana,
    ticker: 'N/A',
    title: 'N/A',
    decimals: 0,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })
  static ethNullCurrency = new CurrencyModel({
    address: CurrencyAddress('null'),
    network: Network.Ethereum,
    ticker: 'N/A',
    title: 'N/A',
    decimals: 0,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })
  static baseNullCurrency = new CurrencyModel({
    address: CurrencyAddress('null'),
    network: Network.Base,
    ticker: 'N/A',
    title: 'N/A',
    decimals: 0,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })
  static bscNullCurrency = new CurrencyModel({
    address: CurrencyAddress('null'),
    network: Network.Bsc,
    ticker: 'N/A',
    title: 'N/A',
    decimals: 0,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })
  static tonNullCurrency = new CurrencyModel({
    address: CurrencyAddress('null'),
    network: Network.Ton,
    ticker: 'N/A',
    title: 'N/A',
    decimals: 0,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static nullCurrency = (network: Network.Network) =>
    match(network)
      .with(Network.Solana, () => CurrencyModel.solNullCurrency)
      .with(Network.Ethereum, () => CurrencyModel.ethNullCurrency)
      .with(Network.Base, () => CurrencyModel.baseNullCurrency)
      .with(Network.Bsc, () => CurrencyModel.bscNullCurrency)
      .with(Network.Ton, () => CurrencyModel.tonNullCurrency)
      .exhaustive()

  // #region Known Currencies
  static Sol = new CurrencyModel({
    address: CurrencyAddress('native'),
    network: Network.Solana,
    ticker: 'SOL',
    title: 'Solana',
    decimals: 9,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: SOL_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static PumpFunSol = new CurrencyModel({
    address: CurrencyAddress('11111111111111111111111111111111'),
    network: Network.Solana,
    ticker: 'SOL',
    title: 'Solana',
    decimals: 9,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: SOL_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static WrappedSol = new CurrencyModel({
    address: CurrencyAddress('So11111111111111111111111111111111111111112'),
    network: Network.Solana,
    ticker: 'WSOL',
    title: 'Wrapped Solana',
    decimals: 9,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: SOL_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static Ether = new CurrencyModel({
    address: CurrencyAddress('native'),
    network: Network.Ethereum,
    ticker: 'ETH',
    title: 'Ether',
    decimals: 18,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: ETH_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static WrappedEther = new CurrencyModel({
    address: CurrencyAddress('0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2'),
    network: Network.Ethereum,
    ticker: 'WETH',
    title: 'Wrapped Ether',
    decimals: 18,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: ETH_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static EtherBase = new CurrencyModel({
    address: CurrencyAddress('native'),
    network: Network.Base,
    ticker: 'ETH',
    title: 'Ether',
    decimals: 18,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: ETH_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static WrappedEtherBase = new CurrencyModel({
    address: CurrencyAddress('0x4200000000000000000000000000000000000006'),
    network: Network.Base,
    ticker: 'WETH',
    title: 'Wrapped Ether',
    decimals: 18,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: ETH_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static Bnb = new CurrencyModel({
    address: CurrencyAddress('native'),
    network: Network.Bsc,
    ticker: 'BNB',
    title: 'BNB',
    decimals: 18,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: ETH_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static WrappedBnb = new CurrencyModel({
    address: CurrencyAddress('0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'),
    network: Network.Bsc,
    ticker: 'WETH',
    title: 'Wrapped Ether',
    decimals: 18,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: ETH_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static Ton = new CurrencyModel({
    address: CurrencyAddress('native'),
    network: Network.Ton,
    ticker: 'TON',
    title: 'Ton',
    decimals: 9,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: TON_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdcSol = new CurrencyModel({
    address: CurrencyAddress('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'),
    network: Network.Solana,
    ticker: 'USDC',
    title: 'USD Coin',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: USDC_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdtSol = new CurrencyModel({
    address: CurrencyAddress('Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'),
    network: Network.Solana,
    ticker: 'USDT',
    title: 'Tether USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: USDT_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static PyusdSol = new CurrencyModel({
    address: CurrencyAddress('2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo'),
    network: Network.Solana,
    ticker: 'PYUSD',
    title: 'PayPal USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdgSol = new CurrencyModel({
    address: CurrencyAddress('2u1tszSeqZ3qBWF3uNGPFc8TzMk2tdiwknnRMWGWjGWH'),
    network: Network.Solana,
    ticker: 'USDG',
    title: 'Global Dollar',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static Usd1Sol = new CurrencyModel({
    address: CurrencyAddress('USD1ttGY1N17NEEHLmELoaybftRBUSErhqYiQzvEmuB'),
    network: Network.Solana,
    ticker: 'USD1',
    title: 'World Liberty Financial USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsxSol = new CurrencyModel({
    address: CurrencyAddress('6FrrzDk5mQARGc1TDYoyVnSyRdds1t4PbtohCD6p3tgG'),
    network: Network.Solana,
    ticker: 'USX',
    title: 'Solstice USX',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static FdusdSol = new CurrencyModel({
    address: CurrencyAddress('9zNQRsGLjNKwCUU5Gq5LR8beUCPzQMVMqKAi3SSZh54u'),
    network: Network.Solana,
    ticker: 'FDUSD',
    title: 'First Digital USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static EurcSol = new CurrencyModel({
    address: CurrencyAddress('HzwqbKZw8HxMN6bF2yFZNrht3c2iXXzpKcFu7uBEDKtr'),
    network: Network.Solana,
    ticker: 'EURC',
    title: 'EURC',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdySol = new CurrencyModel({
    address: CurrencyAddress('A1KLoBrKBde8Ty9qtNQUtq3C2ortoC3u7twggz7sEto6'),
    network: Network.Solana,
    ticker: 'USDY',
    title: 'Ondo US Dollar Yield',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: undefined,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdtEvm = new CurrencyModel({
    address: CurrencyAddress('0x0d4a11d5EEaaC28EC3F61d100daF4d40471f1852'),
    network: Network.Ethereum,
    ticker: 'USDT',
    title: 'Tether USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: USDT_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdtTon = new CurrencyModel({
    address: CurrencyAddress(
      'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs',
    ),
    network: Network.Ton,
    ticker: 'USDT',
    title: 'Tether USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: USDT_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdtBase = new CurrencyModel({
    address: CurrencyAddress('0x0d4a11d5EEaaC28EC3F61d100daF4d40471f1852'),
    network: Network.Base,
    ticker: 'USDT',
    title: 'Tether USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: USDT_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static UsdtBsc = new CurrencyModel({
    address: CurrencyAddress('0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb90'),
    network: Network.Base,
    ticker: 'USDT',
    title: 'Tether USD',
    decimals: 6,
    reusedImageCount: undefined,
    imageHash: undefined,
    socials: Socials.empty(),
    icon: USDT_LOGO,
    iconStatus: 'active',
    moderationReason: undefined,
    supply: BigNumber(0),
    updatedAt: undefined,
  })

  static stableCurrencies = [
    CurrencyModel.UsdcSol,
    CurrencyModel.UsdtSol,
    CurrencyModel.UsdtEvm,
    CurrencyModel.UsdtTon,
    CurrencyModel.UsdtBase,
    CurrencyModel.UsdtBsc,
    CurrencyModel.PyusdSol,
    CurrencyModel.UsdgSol,
    CurrencyModel.Usd1Sol,
    CurrencyModel.UsxSol,
    CurrencyModel.FdusdSol,
    CurrencyModel.UsdySol,
  ]
  // #endregion
}

const titleFromDto = (symbol: string, address: string, name: string) => {
  const isNativeEth = symbol === 'ETH' && address === 'native'
  const isNativeSol = symbol === 'SOL' && address === 'native'

  if (isNativeEth) {
    return 'Ethereum'
  }
  if (isNativeSol) {
    return 'Solana'
  }
  return name
}

const makeIconUrl = (
  url: string | undefined,
  imagesHost: TokenLogosHost,
  symbol: string,
  address: string,
) => {
  const isNativeEth = symbol === 'ETH' && address === 'native'
  const isNativeSol = symbol === 'SOL' && address === 'native'

  if (isNativeEth) {
    return ETH_LOGO
  }
  if (isNativeSol) {
    return SOL_LOGO
  }

  if (!url) {
    return undefined
  }

  if (url.startsWith('http')) {
    return url
  }

  return `${imagesHost}/${url}`
}
